import { MouseEvent, ReactNode } from 'react';
import { OnThisPageItem } from '../../types/onThisPage';

interface OnThisPageProps {
  items: OnThisPageItem[];
  activeId: string | null;
  ariaLabel?: string;
  sticky?: boolean;
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function scrollToSection(event: MouseEvent<HTMLAnchorElement>, id: string) {
  const el = document.getElementById(id);
  if (!el) return;

  event.preventDefault();
  el.scrollIntoView({
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    block: 'start',
  });
  if (window.location.hash !== `#${id}`) {
    history.pushState(null, '', `#${id}`);
  }
}

function tocLinkClass(id: string, activeId: string | null) {
  return `block text-sm py-1 transition-colors ${
    activeId === id
      ? 'text-primary font-semibold border-l-2 border-primary pl-2 -ml-2'
      : 'text-gray-700 hover:text-primary pl-0.5'
  }`;
}

function TocList({
  items,
  activeId,
  nested = false,
}: {
  items: OnThisPageItem[];
  activeId: string | null;
  nested?: boolean;
}) {
  return (
    <ul
      className={
        nested
          ? 'mt-1 space-y-1 pl-3'
          : 'flex flex-col gap-1 sm:flex-row sm:flex-wrap lg:flex-col sm:gap-x-4 sm:gap-y-1 overflow-x-auto pb-1 lg:pb-0'
      }
    >
      {items.map((item) => (
        <li key={item.id} className={nested ? undefined : 'shrink-0'}>
          <a
            href={`#${item.id}`}
            className={tocLinkClass(item.id, activeId)}
            onClick={(event) => scrollToSection(event, item.id)}
          >
            {item.label}
          </a>
          {item.children && item.children.length > 0 ? (
            <TocList items={item.children} activeId={activeId} nested />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function PageSidebar({ children }: { children: ReactNode }) {
  return (
    <div className="mb-8 lg:mb-0 lg:sticky lg:top-32 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
      <div className="flex flex-col gap-8">{children}</div>
    </div>
  );
}

export const PAGE_SIDEBAR_LAYOUT =
  'lg:grid lg:grid-cols-[20rem_minmax(0,1fr)] lg:gap-12 lg:items-start';

export default function OnThisPage({
  items,
  activeId,
  ariaLabel = 'On this page',
  sticky = true,
}: OnThisPageProps) {
  if (items.length === 0) return null;

  return (
    <nav aria-label={ariaLabel} className={sticky ? 'mb-8 lg:mb-0 lg:sticky lg:top-32' : undefined}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
        On this page
      </p>
      <TocList items={items} activeId={activeId} />
    </nav>
  );
}

export const ON_THIS_PAGE_LAYOUT =
  'lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-12 lg:items-start';
